'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import _isEqual from 'lodash/isEqual';

import { EventBatchProvider } from '@/providers/EventBatchProvider';
import { cookieConsentStorageAdapter } from '@/lib/cookieConsentStorage';
import { initCoralogixRum } from '@/lib/coralogix';
import { ELocalStorageKeys, EPlatform, EPlatformType } from '@/lib/enums';
import { PortalBootResponse } from '@/lib/types';
import { AppStateService, appStateService, seedAppState } from '@/state/app.state.service';
import { AppStateContext } from '@/state/AppStateContext';
import { webStorageUtil } from '@/state/webStorage.state.service';
import { DEFAULT_LOCALE } from '@web-store-platforms/locales';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CookieConsentProvider } from '@web-store-platforms/components/cookie-consent';

import dynamic from 'next/dynamic';

import { BootSplash } from '@/components/BootSplash';
import { retryChunkImport } from '@/lib/retryChunkImport';
import { PORTAL_BOOTED_FLAG, announceLandingBoot } from '@/lib/landingBeacon';
import { LocaleProvider } from '@/providers/LocaleProvider';
import { defaultLocale } from '@/i18n/config';
import { writeLanguageCookie } from '@/i18n/languageCookies';
import {
  INTL_TIME_ZONE,
  convertMustachePlaceholders,
  createMessageFallback,
  handleIntlError,
} from '@/i18n/utils';
import baseMessages from '@web-store-platforms/locales/en/common.json';
import overrides from '../../locales/en.overrides.json';
import { deepMerge } from '@web-store-platforms/locales';
import { NextIntlClientProvider } from 'next-intl';

import { Wrapper } from './wrapper';
import { OtpCredentialAuthController } from './components/OtpCredentialAuthController';
import type { IServerOtpLogin } from '@/lib/serverOtpLogin';
import { shouldHoldFullUi, shouldRunOtpCredentialAuth } from './PortalApp.utils';

const CookieConsentGate = dynamic(
  () =>
    retryChunkImport(() =>
      import('./components/CookieConsentGate').then((mod) => mod.CookieConsentGate)
    ),
  { ssr: false }
);

const isServer = typeof window === 'undefined';

// One per document. The server gets one per request instead: a cache shared
// across requests would hand one publisher's pages to another's render.
const browserQueryClient = new QueryClient();

const localEnglishMessages = convertMustachePlaceholders(
  deepMerge(baseMessages, overrides)
) as Record<string, unknown>;

interface IPortalAppProps {
  initialBoot: PortalBootResponse;
  children: React.ReactNode;
  bootRoute: string;
  initialDeviceType: EPlatform;
  initialPlatform: EPlatformType;
  domain: string;
  /** The locale the server rendered in, and whose messages `initialMessages` are. */
  locale: string;
  initialMessages: Record<string, unknown>;
  isOtpCredentialLanding?: boolean;
  /** The OTP login the server is performing for this landing, streamed behind the document. */
  serverOtpLogin?: Promise<IServerOtpLogin | undefined>;
}

export const PortalApp: React.FC<IPortalAppProps> = ({
  initialBoot,
  children,
  bootRoute,
  initialDeviceType,
  initialPlatform,
  domain,
  locale,
  initialMessages,
  isOtpCredentialLanding = false,
  serverOtpLogin,
}) => {
  // Seeded before the first render reads it, on both sides of hydration.
  const [appState] = useState((): AppStateService => {
    const service = isServer ? new AppStateService() : appStateService;
    seedAppState(service, {
      initialBoot,
      deviceType: initialDeviceType,
      platform: initialPlatform,
      domain,
      currentLanguage: locale,
    });

    return service;
  });
  const [queryClient] = useState(() => (isServer ? new QueryClient() : browserQueryClient));
  const prevBootRef = useRef<PortalBootResponse | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [otpAuthSettled, setOtpAuthSettled] = useState(false);

  const onOtpAuthSettled = useCallback((): void => {
    setOtpAuthSettled(true);
  }, []);

  useEffect(() => {
    setIsMounted(true);
    announceLandingBoot(PORTAL_BOOTED_FLAG);
  }, []);

  useEffect(() => {
    if (_isEqual(prevBootRef.current, initialBoot)) return;
    prevBootRef.current = initialBoot;

    appState.setInitialBoot(initialBoot);

    if (initialBoot?.stores?.[0]) {
      webStorageUtil.set(ELocalStorageKeys.CURRENT_STORE_ID, initialBoot.stores[0]);
    }
    if (initialBoot?.portalId) {
      webStorageUtil.set(ELocalStorageKeys.PORTAL_ID, initialBoot.portalId);
    }

    // The server resolved the language with the LocaleProvider's own rule;
    // adopting it (rather than re-deriving one from storage) keeps the language
    // from changing between hydration and the provider's resolution.
    const savedLanguage = webStorageUtil.get(ELocalStorageKeys.CURRENT_LANGUAGE);
    const languageToUse = locale || savedLanguage || initialBoot?.locale || DEFAULT_LOCALE;

    appState.setCurrentLanguage(languageToUse);

    if (!savedLanguage) {
      webStorageUtil.set(ELocalStorageKeys.CURRENT_LANGUAGE, languageToUse);
    }
    writeLanguageCookie(savedLanguage || languageToUse);
  }, [initialBoot, bootRoute, appState, locale]);

  useEffect(() => {
    appState.setDeviceType(initialDeviceType);
  }, [initialDeviceType, appState]);

  useEffect(() => {
    appState.setPlatform(initialPlatform);
  }, [initialPlatform, appState]);

  useEffect(() => {
    appState.setDomain(domain);
  }, [domain, appState]);

  useEffect(() => {
    initCoralogixRum(initialBoot);
  }, [initialBoot]);

  const otpAuthGateState = {
    isOtpCredentialLanding,
    isMounted,
    isOtpAuthSettled: otpAuthSettled,
  };
  const holdFullUi = shouldHoldFullUi(otpAuthGateState);
  const englishFallback = createMessageFallback(localEnglishMessages);

  return (
    <AppStateContext.Provider value={appState}>
      <QueryClientProvider client={queryClient}>
        {shouldRunOtpCredentialAuth(otpAuthGateState) ? (
          <NextIntlClientProvider
            locale={defaultLocale.isoCode}
            messages={localEnglishMessages}
            timeZone={INTL_TIME_ZONE}
            onError={handleIntlError}
            getMessageFallback={englishFallback}
          >
            <OtpCredentialAuthController
              bootRoute={bootRoute}
              onSettled={onOtpAuthSettled}
              serverOtpLogin={serverOtpLogin}
            />
          </NextIntlClientProvider>
        ) : null}
        {/* Server-rendered with the document, so the held page spins instead of staying blank. */}
        {holdFullUi ? (
          <BootSplash backgroundColor={initialBoot?.theme?.general?.backgroundColor?.colorOne} />
        ) : null}
        {!holdFullUi ? (
          <LocaleProvider initialLocale={locale} initialMessages={initialMessages}>
            <EventBatchProvider>
              <CookieConsentProvider storageAdapter={cookieConsentStorageAdapter}>
                {/* Rendered on the server too: the document carries the page, not a spinner. */}
                <Wrapper
                  initialPlatform={initialPlatform}
                  initialDeviceType={initialDeviceType}
                  domain={domain}
                  isOtpCredentialLanding={isOtpCredentialLanding}>
                  {children}
                </Wrapper>
                <CookieConsentGate />
              </CookieConsentProvider>
            </EventBatchProvider>
          </LocaleProvider>
        ) : null}
      </QueryClientProvider>
    </AppStateContext.Provider>
  );
};
